import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
  Accordion, AccordionContent, AccordionItem, AccordionTrigger,
} from "@/components/ui/accordion";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { Layers, RefreshCw, List, LayoutGrid, Search } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const DataGrouping = () => {
  const [taskGroups, setTaskGroups] = useState([]);
  const [allRecords, setAllRecords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [viewMode, setViewMode] = useState("grouped");
  const [listSearch, setListSearch] = useState("");
  const [taskTypeFilter, setTaskTypeFilter] = useState("");

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const taskRes = await axios.get(`${API}/grouped/by-task`);
      const groups = taskRes.data.groups || [];
      setTaskGroups(groups);

      const flat = [];
      groups.forEach(g => {
        (g.records || []).forEach(r => flat.push({ ...r, group_task: g.task }));
      });
      setAllRecords(flat);
    } catch {
      toast.error("Failed to load grouped data");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const filteredRecords = allRecords.filter(r => {
    if (taskTypeFilter && (r.group_task || r.task || "") !== taskTypeFilter) return false;
    if (!listSearch) return true;
    const s = listSearch.toLowerCase();
    return (r.main_title || "").toLowerCase().includes(s)
      || (r.episode_title || "").toLowerCase().includes(s)
      || (r.atlas_job_id || "").toLowerCase().includes(s)
      || (r.po_number || "").toLowerCase().includes(s)
      || (r.resource || "").toLowerCase().includes(s)
      || (r.group_task || "").toLowerCase().includes(s);
  });

  const taskTypes = [...new Set(allRecords.map(r => r.group_task || r.task || "").filter(Boolean))].sort();
  const totalAmount = allRecords.reduce((s, r) => s + (r.amount || 0), 0);

  if (loading) {
    return (
      <div className="page-container">
        <div className="flex items-center justify-center h-64"><div className="spinner" /></div>
      </div>
    );
  }

  return (
    <div className="page-container" data-testid="data-grouping-page">
      <div className="page-header flex items-center justify-between">
        <div>
          <h1 className="page-title">Tasks</h1>
          <p className="page-subtitle">
            {allRecords.length} tasks &middot; {"\u20B9"}{totalAmount.toLocaleString("en-IN", { minimumFractionDigits: 2 })}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <div className="flex items-center bg-slate-100 rounded-lg p-0.5" data-testid="view-toggle">
            <button
              onClick={() => setViewMode("grouped")}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium transition-all ${viewMode === "grouped" ? "bg-white text-slate-800 shadow-sm" : "text-slate-500 hover:text-slate-700"}`}
              data-testid="view-grouped"
            >
              <LayoutGrid className="w-3.5 h-3.5" /> Grouped
            </button>
            <button
              onClick={() => setViewMode("list")}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium transition-all ${viewMode === "list" ? "bg-white text-slate-800 shadow-sm" : "text-slate-500 hover:text-slate-700"}`}
              data-testid="view-list"
            >
              <List className="w-3.5 h-3.5" /> List
            </button>
          </div>
          <Button onClick={fetchData} variant="outline" className="btn-secondary" data-testid="refresh-grouping-btn">
            <RefreshCw className="w-4 h-4 mr-2" />Refresh
          </Button>
        </div>
      </div>

      {/* ===== GROUPED VIEW ===== */}
      {viewMode === "grouped" && (
        <>
          {taskGroups.length > 0 ? (
            <div className="space-y-4">
              {taskGroups.map((group, index) => (
                <Card key={index} className="card overflow-hidden">
                  <Accordion type="single" collapsible>
                    <AccordionItem value={`task-${index}`} className="border-0">
                      <AccordionTrigger className="px-6 py-4 hover:no-underline hover:bg-slate-50">
                        <div className="flex items-center justify-between w-full pr-4">
                          <div className="flex items-center gap-4">
                            <div className="w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center">
                              <Layers className="w-5 h-5 text-blue-600" />
                            </div>
                            <div className="text-left">
                              <h3 className="font-semibold text-slate-900">{group.task}</h3>
                              <span className="text-sm text-slate-500">{group.count} tasks</span>
                            </div>
                          </div>
                          <div className="flex items-center gap-6 text-right">
                            <div>
                              <p className="text-sm text-slate-500">Duration</p>
                              <p className="font-semibold text-slate-900">{(group.total_duration || 0).toFixed(2)}</p>
                            </div>
                            <div>
                              <p className="text-sm text-slate-500">Amount</p>
                              <p className="font-semibold text-emerald-600">{"\u20B9"}{(group.total_amount || 0).toFixed(2)}</p>
                            </div>
                          </div>
                        </div>
                      </AccordionTrigger>
                      <AccordionContent className="px-0 pb-0">
                        {group.records.length > 0 ? (
                          <ScrollArea className="h-[300px]">
                            <div
                              className="grid bg-white border-b border-slate-100 sticky top-0 z-10"
                              style={{ gridTemplateColumns: '1fr 1fr 1.5fr 1fr 1fr 1fr', padding: '8px 20px' }}
                            >
                              <div className="text-[10px] font-semibold text-slate-400 uppercase tracking-wider text-center">Atlas Job ID</div>
                              <div className="text-[10px] font-semibold text-slate-400 uppercase tracking-wider text-center">PO Number</div>
                              <div className="text-[10px] font-semibold text-slate-400 uppercase tracking-wider text-center">Resource</div>
                              <div className="text-[10px] font-semibold text-slate-400 uppercase tracking-wider text-center">Duration</div>
                              <div className="text-[10px] font-semibold text-slate-400 uppercase tracking-wider text-center">Rate</div>
                              <div className="text-[10px] font-semibold text-slate-400 uppercase tracking-wider text-center">Amount</div>
                            </div>
                            {group.records.map((record, idx) => (
                              <div
                                key={idx}
                                className="grid border-b border-slate-100 hover:bg-slate-50/50 transition-colors"
                                style={{ gridTemplateColumns: '1fr 1fr 1.5fr 1fr 1fr 1fr', padding: '10px 20px', alignItems: 'center' }}
                                data-testid={`grouping-row-${record.id}`}
                              >
                                <div className="text-sm font-mono text-center">{record.atlas_job_id || "-"}</div>
                                <div className="text-sm font-mono text-center">{record.po_number || "-"}</div>
                                <div className="text-sm text-center leading-snug" style={{ wordBreak: 'break-word' }}>{record.resource || "-"}</div>
                                <div className="text-sm font-mono text-center">{record.duration_words?.toFixed(2) || "-"}</div>
                                <div className="text-sm font-mono text-center">{record.rate?.toFixed(2) || "-"}</div>
                                <div className="text-sm font-mono text-center text-emerald-600 font-semibold">{"\u20B9"}{record.amount?.toFixed(2) || "-"}</div>
                              </div>
                            ))}
                          </ScrollArea>
                        ) : (
                          <div className="py-8 text-center text-slate-400">
                            <p className="text-sm">No tasks with this type yet</p>
                          </div>
                        )}
                      </AccordionContent>
                    </AccordionItem>
                  </Accordion>
                </Card>
              ))}
            </div>
          ) : (
            <Card className="card p-12 text-center">
              <Layers className="w-12 h-12 text-slate-300 mx-auto mb-4" />
              <p className="text-lg font-medium text-slate-500">No task groups found</p>
              <p className="text-sm text-slate-400 mt-1">Sync data from Database Sync to see task groupings</p>
            </Card>
          )}
        </>
      )}

      {/* ===== LIST VIEW ===== */}
      {viewMode === "list" && (
        <Card className="card overflow-hidden">
          <div className="px-4 py-2.5 border-b border-slate-200 flex items-center gap-2 flex-wrap">
            <select
              value={taskTypeFilter}
              onChange={e => setTaskTypeFilter(e.target.value)}
              className="h-8 px-2 text-xs border border-slate-200 rounded-md bg-white text-slate-700 focus:outline-none focus:ring-1 focus:ring-blue-500 min-w-[100px]"
              data-testid="task-type-filter"
            >
              <option value="">All Types</option>
              {taskTypes.map(t => <option key={t} value={t}>{t}</option>)}
            </select>
            <div className="relative flex-1 max-w-xs">
              <Search className="absolute left-2.5 top-2 w-3.5 h-3.5 text-slate-400" />
              <Input
                value={listSearch}
                onChange={e => setListSearch(e.target.value)}
                placeholder="Search title, PO, job ID..."
                className="pl-8 h-8 text-xs"
                data-testid="list-search"
              />
            </div>
            <Badge className="bg-slate-100 text-slate-600 border-0 text-[10px] px-2 py-0.5">{filteredRecords.length} records</Badge>
            <Badge className="bg-emerald-50 text-emerald-700 border-0 text-[10px] px-2 py-0.5">
              {"\u20B9"}{filteredRecords.reduce((s, r) => s + (r.amount || 0), 0).toLocaleString("en-IN", { minimumFractionDigits: 2 })}
            </Badge>
          </div>
          <div className="max-h-[calc(100vh-260px)] overflow-auto">
            <Table>
              <TableHeader>
                <TableRow className="bg-slate-50 sticky top-0 z-10">
                  <TableHead className="text-[10px] font-semibold uppercase w-[180px] px-2">Title / Episode</TableHead>
                  <TableHead className="text-[10px] font-semibold uppercase text-center w-16 px-1">Type</TableHead>
                  <TableHead className="text-[10px] font-semibold uppercase text-center w-20 px-1">PO</TableHead>
                  <TableHead className="text-[10px] font-semibold uppercase text-center w-24 px-1">Job ID</TableHead>
                  <TableHead className="text-[10px] font-semibold uppercase w-[140px] px-2">Resource</TableHead>
                  <TableHead className="text-[10px] font-semibold uppercase text-right w-16 px-1">Dur.</TableHead>
                  <TableHead className="text-[10px] font-semibold uppercase text-right w-14 px-1">Rate</TableHead>
                  <TableHead className="text-[10px] font-semibold uppercase text-right w-20 px-2">Amount</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredRecords.map((r, idx) => (
                  <TableRow key={r.id || idx} className="hover:bg-slate-50/50" data-testid={`list-row-${r.id}`}>
                    <TableCell className="px-2 py-1.5">
                      <p className="text-xs font-medium text-slate-700 leading-tight line-clamp-2" style={{ wordBreak: 'break-word' }}>{r.main_title || "-"}</p>
                      <p className="text-[10px] text-slate-400 leading-tight mt-0.5 line-clamp-1" style={{ wordBreak: 'break-word' }}>{r.episode_title || ""} {r.episode_number ? `Ep ${r.episode_number}` : ""}</p>
                    </TableCell>
                    <TableCell className="text-center px-1 py-1.5">
                      <span className="inline-flex px-1.5 py-0.5 bg-blue-100 text-blue-700 rounded text-[9px] font-semibold">{r.group_task || r.task || "-"}</span>
                    </TableCell>
                    <TableCell className="text-center text-[11px] font-mono text-slate-600 px-1 py-1.5">{r.po_number || "-"}</TableCell>
                    <TableCell className="text-center text-[11px] font-mono text-slate-600 px-1 py-1.5">{r.atlas_job_id || "-"}</TableCell>
                    <TableCell className="text-[11px] text-slate-600 px-2 py-1.5 truncate max-w-[140px]">{r.resource || "-"}</TableCell>
                    <TableCell className="text-right text-[11px] font-mono text-slate-600 px-1 py-1.5">{(r.duration_words || 0).toFixed(2)}</TableCell>
                    <TableCell className="text-right text-[11px] font-mono text-slate-600 px-1 py-1.5">{"\u20B9"}{(r.rate || 0).toFixed(0)}</TableCell>
                    <TableCell className="text-right text-xs font-mono font-semibold text-emerald-600 px-2 py-1.5">{"\u20B9"}{(r.amount || 0).toFixed(2)}</TableCell>
                  </TableRow>
                ))}
                {filteredRecords.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={8} className="text-center py-10 text-sm text-slate-400">No matching records</TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </div>
        </Card>
      )}
    </div>
  );
};

export default DataGrouping;
